import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ATTRIBUTE_KINDS, ATTRIBUTE_TYPES, DISPLAY_FORMATS, isDisplayFormatAllowed } from '@/entities/attributes';
import { zodCamelCaseString } from '@/shared/lib';
import { TFunction } from 'i18next';

const createSchema = (t: TFunction<readonly ['attributes', 'common']>) => {
  const structureAttributeSchema = z
    .object({
      code: z
        .string()
        .refine(
          (val) => {
            // If provided and non-empty, must be in camelCase
            if (val && val.length > 0) {
              return /^[a-z][a-zA-Z0-9]*$/.test(val);
            }
            return true;
          },
          { message: t('common:codeMustBeCamelCase') },
        )
        .optional(),
      name: z.string().nullable().optional(),
      dataType: z.string().nullable().optional(),
      isList: z.boolean().nullable().optional(),
      description: z.string().nullable().optional(),
      displayFormat: z.nativeEnum(DISPLAY_FORMATS).nullable().optional(),
      type: z.nativeEnum(ATTRIBUTE_KINDS).nullable().optional(),
      rule: z.string().nullable().optional(),
      tooltip: z.string().nullable().optional(),
      /**
       * Признак нового атрибута, создаваемого inline внутри структуры.
       * - `true` — атрибут создан через кнопку «Добавить новый атрибут» и ещё не существует в системе
       * - `false` — атрибут добавлен из существующих (addExistingAttribute) или загружен из initial при редактировании
       */
      isNew: z.boolean().optional(),
    })
    // У создаваемого внутри структуры атрибута тип данных обязателен наравне с карточкой отдельного
    // атрибута: он не предзаполняется, поэтому без явного выбора форма не сохраняется.
    // У добавленного из существующих тип уже задан и на правку закрыт — его не проверяем
    .superRefine((attribute, ctx) => {
      if (attribute.isNew && !attribute.dataType) {
        ctx.addIssue({
          code: 'custom',
          message: t('common:required'),
          path: ['dataType'],
        });
      }
      // Формат отображения принадлежит типу данных: несочетаемую пару сервер отклонит
      if (!isDisplayFormatAllowed(attribute.dataType, attribute.displayFormat)) {
        ctx.addIssue({
          code: 'custom',
          message: t('attributes:displayFormat.notAllowedForType'),
          path: ['displayFormat'],
        });
      }
    });

  const structureGroupItemSchema = z.object({
    code: z.string().optional(),
    value: z.string(),
    description: z.string().optional().nullable(),
  });

  const structureGroupSchema = z.object({
    items: z.record(z.string(), structureGroupItemSchema),
  });

  const structureSchema = z.object({
    code: z
      .string()
      .optional()
      .refine(
        (val) => {
          // If provided, must be non-empty and in correct format
          if (val && val.length > 0) {
            return /^[A-Z_0-9]+$/.test(val);
          }
          return true;
        },
        { message: t('common:codeMustBeCapsLock') },
      ),
    name: z.string().optional(),
    description: z.string().nullable().optional(),
    isSelect: z.boolean().nullable().optional(),
    attributes: z.array(structureAttributeSchema).optional(),
    groups: z.array(structureGroupSchema).nullable().optional(),
  });

  const schemaBase = z
    .object({
      name: z
        .string(t('common:required'))
        .min(1, t('common:required'))
        .max(150, t('common:maxLength', { count: 150 })),
      code: zodCamelCaseString({
        message: t('common:codeMustBeCamelCase'),
        minLength: 1,
        minLengthMessage: t('common:required'),
      }),
      dataType: z.enum(ATTRIBUTE_TYPES, t('common:required')),
      type: z.nativeEnum(ATTRIBUTE_KINDS).nullable().default(null),
      rule: z.string().nullable().default(null),
      description: z.string(t('common:required')).min(1, t('common:required')),
      tooltip: z.string().nullable().default(''),
      isList: z.boolean().default(false),
      showWhenEmpty: z.boolean().default(false),
      displayFormat: z.nativeEnum(DISPLAY_FORMATS).nullable().default(null),
      selectValues: z
        .array(
          z.object({
            name: z.string(t('common:required')).min(1, t('common:required')),
            description: z.string(t('common:required')).min(1, t('common:required')),
            code: zodCamelCaseString({
              message: t('common:codeMustBeCamelCase'),
              minLength: 1,
              minLengthMessage: t('common:required'),
            }),
          }),
        )
        .optional(),
      attributeGroups: z
        .array(
          z.object({
            code: z.string(),
            name: z.string(),
            description: z.string(),
          }),
        )
        .default([]),
      structure: structureSchema.nullable().default(null),
    })
    .refine(
      (data) => {
        // Only validate structure for STRUCTURE type
        if (data.dataType !== 'STRUCTURE') {
          return true;
        }

        // For STRUCTURE type, structure fields are optional
        // But if any field is filled, all required fields must be complete
        if (data.structure) {
          const code = (data.structure.code || '').trim();
          const name = (data.structure.name || '').trim();
          const attributes = data.structure.attributes || [];

          // Check if at least one field has content
          const hasAnyContent = code.length > 0 || name.length > 0 || attributes.length > 0;

          // If structure has any content, all required fields must be filled
          if (hasAnyContent) {
            if (code.length === 0) {
              return false;
            }
            if (name.length === 0) {
              return false;
            }
            if (attributes.length === 0) {
              return false;
            }
          }
        }
        return true;
      },
      {
        message: t('attributes:structureIncomplete'),
        path: ['structure'],
      },
    )
    .refine(
      (data) => {
        // If type is SELECT or MULTISELECT, at least one selectValue is required
        if (data.dataType === 'SELECT' || data.dataType === 'MULTISELECT') {
          if (!data.selectValues || data.selectValues.length === 0) {
            return false;
          }
        }
        return true;
      },
      {
        message: t('attributes:selectValuesRequired'),
        path: ['selectValues'],
      },
    )
    // Формат отображения принадлежит типу данных: несочетаемую пару сервер отклонит
    .refine((data) => isDisplayFormatAllowed(data.dataType, data.displayFormat), {
      message: t('attributes:displayFormat.notAllowedForType'),
      path: ['displayFormat'],
    });

  return schemaBase;
};

export type AttributeFormValues = z.infer<ReturnType<typeof createSchema>>;

export const useAttributeSchema = () => {
  const { t } = useTranslation(['attributes', 'common']);

  return createSchema(t);
};
